import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { fnGetWhLocLotCtPltInv, fnGetWhSnInv } from "@shared/utils/InvUtils";
import {
  getInitFormData,
  applyAutoSlip,
  fnGetIotype,
} from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import SnButton from "@shared/components/SnButton";
import SnResizeBar from "@shared/components/SnResizeBar";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { dateToString } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-community";
import SnBarcodeBox from "@shared/components/SnBarcodeBox";
import SnSwitch, { SnSwitchHandle } from "@shared/components/SnSwitch";
import SnNumberBox from "@shared/components/SnNumberBox";

interface Props {
  Menuprops: menuInfo;
}

const SN_MTL_MOW_REG_007 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
    grid4: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const txtBarcodeRef = useRef<any>(null);
  const numberBoxFifoRef = useRef<any>(null);

  const searchRef = useRef<any>(null);
  const { userInfo } = global(); // Login user 정보
  const iotypeRef = useRef<any[]>([]);
  //#endregion

  // 재고리스트
  const [lotSnData, setLotSnData] = useState<Record<string, any>[]>([]);
  const lotSnDataRef = useRef<Record<string, any>[]>([]);

  // 스위치
  const switchRef = useRef<SnSwitchHandle>(null);

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid4: [],
  });
  const [selectedDocu, setSelectedDocu] = useState<string>("");
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          grid4: rtn.grid.grid4,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
      onActivate(Menuprops.nodeData);
    }
  }, [columns, Menuprops]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await fnGetIotype("MTL", "2", "5", "");
      iotypeRef.current = data;
    };
    fetchData();
  }, []);

  useEffect(() => {
    lotSnDataRef.current = lotSnData;
  }, [lotSnData]);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    const modifiedRowsH = getGridCurrent("grid1").getChange();
    const modifiedRowsL = getGridCurrent("grid2").getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_mtl_mow_reg_007_01",
        params: searchDataToUse,
      };

      setLotSnData([]);

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: [],
        grid4: data[2] || [],
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region onActivate
  const onActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops.figure as string) === "fg_drilldown.Search") {
      const selectedData = {
        cd_firm: nodeData.cd_firm,
        cd_biz: nodeData.cd_biz,
        no_req: nodeData.no_req,
        dt_from: nodeData.dt_req,
        dt_to: nodeData.dt_req,
      };
      const searchData = {
        ...formData,
        ...selectedData,
      };
      setFormData(searchData);
      onSearch(searchData);
    }
  };
  //#endregion

  //#region onInsert
  const onInsert = () => {
    try {
      const addedNode = getGridCurrent("grid1").addRow();
      addedNode?.setDataValue("cd_io", addedNode.data?.cd_io);
    } catch (error: any) {}
  };
  //#endregion

  //#region onAddRow
  const onAddRow = () => {
    try {
      return getGridCurrent("grid2").addRow();
    } catch (error: any) {}
  };
  //#endregion

  //#region onDelete
  const onDelete = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    let hasInvalid = false;

    getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
      if (node.data.yn_req === "Y") {
        hasInvalid = true;
      }
    });

    if (hasInvalid) {
      showDialog(
        "alter",
        "해당 메뉴에서 삭제할 수 없는 내역입니다. [자재불출인계처리] 메뉴에서 취소하시기 바랍니다."
      );
      return;
    }

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    if (!focusedRowNodeH) {
      await showDialog("alter", "선택된 행이 없습니다.");
      return false;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    // 삭제할 헤더의 no_req로 출고정보를 필터한다.
    getGridCurrent("grid4").applyFilters({
      no_req: focusedRowNodeH.data.no_req,
    });

    getGridCurrent("grid4").deleteAllRows();
    getGridCurrent("grid2").deleteAllRows();
    getGridCurrent("grid1").deleteSelectedRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      return;
    }

    return await onSave();
  };
  //endregion

  //#region OnDeleteRow
  const onDeleteRow = () => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }

    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();
    if (focusedRowNodeL.data.yn_req === "Y") {
      showDialog(
        "alter",
        "해당 메뉴에서 삭제할 수 없는 내역입니다. [자재불출인계처리] 메뉴에서 취소하시기 바랍니다."
      );
      return;
    }

    const insertRows = getGridCurrent("grid2").getChange().insert ?? [];
    const isInsertRow = insertRows.some(
      (row: any) =>
        row.no_line === focusedRowNodeL.data.no_line &&
        row.no_req === focusedRowNodeL.data.no_req
    );

    if (isInsertRow) {
      resetRowData();
    } else {
      getGridCurrent("grid4").deleteAllRows();
    }

    getGridCurrent("grid2").deleteSelectedRows();

    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      getGridCurrent("grid3").applyFilters({
        cd_item: "",
        cd_wh: "",
      });
    }
  };
  //#endregion

  //#region onSave
  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();
      getGridCurrent("grid2").stopEditing();
      // H 필수값 체크
      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      // L 필수값 체크
      const requiredValuesCheckL =
        getGridCurrent("grid2").requiredValuesCheck();
      if (!requiredValuesCheckL) {
        return false;
      }

      const isureqhChanges = getGridCurrent("grid1").getChange();
      const isureqlChanges = getGridCurrent("grid2").getChange();
      const reqlotsnChanges = getGridCurrent("grid4").getChange();

      const headerInserts = isureqhChanges.insert ?? [];
      const lineInserts = isureqlChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (
        isureqhChanges.modifiedRowcount === 0 &&
        isureqlChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      const noSlip = await applyAutoSlip({
        cd_column: "no_req",
        cd_module: "MTL",
        no_line: 11,
        dt: focusedRowNodeH.data.dt_req,
        girdrefs: [
          gridRefs.current["grid1"],
          gridRefs.current["grid2"],
          gridRefs.current["grid4"],
        ],
        inserts: [isureqhChanges, isureqlChanges, reqlotsnChanges],
        cd_menu: Menuprops.cd_menu,
      });

      // 요청 데이터 구성
      const SaveData = {
        isureqh: isureqhChanges,
        isureql: isureqlChanges,
        req_lotsn: reqlotsnChanges,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_MTL_MOW_REG_007_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      setLotSnData([]);
      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      getGridCurrent("grid4").resetUpdate(true);
      return false;
    }
  };
  //#endregion

  //#region GridH Events
  const onBeforeAddRowH = (): any => {
    try {
      const modifiedRowsH = getGridCurrent("grid1").getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        showDialog("warning", "수정된 내용이 있습니다.");
        return false;
      }
      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        cd_biz: searchInfo?.cd_biz,
        cd_dept: userInfo?.cd_dept,
        nm_dept: userInfo?.nm_dept,
        cd_gp: userInfo?.cd_gp,
        cd_emp: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        dt_req: today,
        cd_io: "top1",
        tp_slip: "5",
        fg_slip: "2",
        no_req: "",
        cd_wh: "",
        st_req: "F",
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onCellValueChangedH = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    const rowCount = getGridCurrent("grid2").getDisplayedRowCount();

    switch (colId) {
      case "cd_io":
        const ioType = iotypeRef.current.find(
          (item) => item.cd_io === rowData.cd_io
        );

        rowNode.setDataValue("fg_io", ioType.fg_io);
        rowNode.setDataValue("fg_money", ioType.fg_money);

        if (rowCount > 0) {
          getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
            node.setDataValue("cd_io", rowData.cd_io);
            node.setDataValue("fg_io", ioType.fg_io);
            node.setDataValue("fg_money", ioType.fg_money);
          });
        }
        break;
      case "dt_req":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue(colId, rowData.cd_wh);
        });
        break;
      case "cd_wh":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue(colId, rowData.cd_wh);
        });
        break;
    }
  };

  const onFocusedRowChangedH = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      const no_req = selectedRow.no_req;
      getGridCurrent("grid2").selectAndFocusRow(0);
      setSelectedDocu(no_req);

      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();

      if (rowCount > 0) {
        getGridCurrent("grid2").clearFocus();
        getGridCurrent("grid2").selectAndFocusRow(0);
      } else {
        getGridCurrent("grid4").applyFilters({
          no_req: "",
          no_line: "",
        });
      }
    } else {
      return;
    }
  };
  //#endregion

  //#region GridL Events
  const onBeforeAddRowL = (): any => {
    try {
      getGridCurrent("grid1").stopEditing();
      const focusedNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }
      const maxNoLine = getGridCurrent("grid2").getMaxValue("no_line");
      const updatedRow = {
        cd_firm: focusedNodeH.data?.cd_firm,
        cd_biz: focusedNodeH.data?.cd_biz,
        dt_req: focusedNodeH.data?.dt_req,
        no_line: maxNoLine + 1,
        no_req: focusedNodeH.data?.no_req,
        qt: 0,
        qt_good: 0,
        qt_bad: 0,
        qt_req_rev: 0,
        cd_wh: focusedNodeH.data?.cd_wh,
        nm_wh: focusedNodeH.data?.nm_wh,
        yn_req: "N",
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onFocusedRowChangedL = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      if (selectedRow.state === "insert") {
        getGridCurrent("grid3").applyFilters({
          cd_item: selectedRow.cd_item,
          cd_wh: selectedRow.cd_wh,
          qt_inv: "notZero",
        });
      } else {
        getGridCurrent("grid3").applyFilters({
          cd_item: "",
          cd_wh: "",
        });
      }
    } else {
      getGridCurrent("grid3").applyFilters({
        cd_item: "",
        cd_wh: "",
      });
    }
  };

  const onCellValueChangedL = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    switch (colId) {
      case "cd_wh":
        const rowCount = getGridCurrent("grid2").getDisplayedRowCount();
        if (rowCount === 1) return;
        if (!rowData.cd_wh) return;

        let apply = false;
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          if (
            node.data[colId] === "" ||
            node.data[colId] === null ||
            node.data[colId] === undefined
          )
            apply = true;
        });
        if (!apply) return;

        const confirm = await showDialog(
          "confirm",
          "일괄적용하시겠습니까?(비어있는 칸이 채워집니다.)"
        );
        if (confirm) {
          const visibleIdx = rowNode.rowIndex;

          getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
            if (
              node.data.cd_wh === "" ||
              node.data.cd_wh === null ||
              node.data.cd_wh === undefined
            ) {
              node.setDataValue("cd_wh", rowData.cd_wh);
              node.setDataValue("nm_wh", rowData.nm_wh);
            }
          });
          if (visibleIdx) {
            getGridCurrent("grid2").selectAndFocusRow(visibleIdx);
          }
        }
        break;
      // 품목바뀌면 수량 초기화
      case "cd_item":
        rowData.qt = 0;
        rowData.qt_good = 0;
        rowData.qt_bad = 0;
        rowData.qt_req_rev = 0;
        break;
      case "qt_good":
      case "qt_bad":
        rowNode.setDataValue("qt", rowData.qt_good + rowData.qt_bad, "noEvent");
        rowNode.setDataValue(
          "qt_req_rev",
          rowData.qt_good + rowData.qt_bad,
          "noEvent"
        );
        break;
    }

    switch (colId) {
      case "cd_item":
      case "cd_wh":
      case "nm_wh":
        if (
          rowData.cd_item !== "" &&
          rowData.cd_item !== null &&
          rowData.cd_item !== undefined &&
          rowData.cd_wh !== "" &&
          rowData.cd_wh !== null &&
          rowData.cd_wh !== undefined
        ) {
          rowData.qt = 0;
          rowData.qt_req_rev = 0;
          rowData.qt_good = 0;
          rowData.qt_bad = 0;
          resetRowData();

          const data = {
            cd_biz: rowData.cd_biz,
            cd_item: rowData.cd_item,
            cd_wh: rowData.cd_wh,
            cd_loc: "",
          };

          if (
            !lotSnDataRef.current.some(
              (item) =>
                item.cd_item === rowData.cd_item && item.cd_wh === rowData.cd_wh
            )
          ) {
            switch (rowData.fg_unit) {
              case "LOT":
                const searchLotData = await fnGetWhLocLotCtPltInv(data);
                const lotData = searchLotData.map((item: any) => {
                  const { no_lot, ...rest } = item; // no_lot만 추출

                  return {
                    ...rest,
                    no_barcode: no_lot, // 필드명 바꾸기
                    fg_unit: "LOT", // LOT/SN구분
                    qt_isu: 0, // 적용량 추가
                  };
                });

                // 이전 데이터에 새로 조회한 데이터 합치기
                setLotSnData((prevData) => {
                  // 이미 해당 품목+창고 데이터가 prevData에 있는지 다시 한번 확인 (상태 업데이트 시점의 최신값 기준)
                  const isAlreadyExist = prevData.some(
                    (item) =>
                      item.cd_item === rowData.cd_item &&
                      item.cd_wh === rowData.cd_wh
                  );

                  if (isAlreadyExist) return prevData; // 있으면 추가 안 함
                  return [...prevData, ...lotData];
                });
                break;
              case "SN":
                const searchSnData = await fnGetWhSnInv(data);
                const snData = searchSnData.map((item: any) => {
                  const { no_sn, ...rest } = item;

                  return {
                    ...rest,
                    no_barcode: no_sn, // 필드명 바꾸기
                    fg_unit: "SN", // LOT/SN구분
                    no_src: item.no_io, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                    no_line_src: item.no_line, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                    qt_isu: 1, // 적용량 추가
                    no_origin: item.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
                    no_line_origin: item.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
                  };
                });

                // 이전 데이터에 새로 조회한 데이터 합치기
                setLotSnData((prevData) => {
                  // 이미 해당 품목+창고 데이터가 prevData에 있는지 다시 한번 확인 (상태 업데이트 시점의 최신값 기준)
                  const isAlreadyExist = prevData.some(
                    (item) =>
                      item.cd_item === rowData.cd_item &&
                      item.cd_wh === rowData.cd_wh
                  );

                  if (isAlreadyExist) return prevData; // 있으면 추가 안 함
                  return [...prevData, ...snData];
                });
                break;
            }
          }
          // 재고리스트 필터돌게
          getGridCurrent("grid2").clearFocus();
          getGridCurrent("grid2").selectAndFocusRow(rowNode.rowIndex);
        } else {
          resetRowData();
        }
        break;
    }
  };

  const onGridLBtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        onAddRow();
        break;
      case "deleteRow":
        onDeleteRow();
        break;
    }
  };
  //#endregion

  //#region onCellValueChangedLotSn
  const onCellValueChangedLotSn = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "qt_isu") {
      if (rowData.qt_inv < rowData.qt_isu) {
        showDialog("alter", "재고량을 초과했습니다.");
        rowNode.setDataValue("qt_isu", 0);
      }
    }
  };

  const onRowDataUpdatedLotSn = () => {
    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    // 신규일 때만 바코드 컴포넌트 포커스 타게
    if (focusedRowNodeL) {
      if (focusedRowNodeL.data.state === "insert") {
        txtBarcodeRef.current.focus();
      }
    }
  };
  //#endregion

  //#region GridD Events
  const onGridDBtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        selectInv("C", "");
        break;
      case "deleteRow":
        if (getGridCurrent("grid4").getDisplayedRowCount() < 1) {
          return;
        }

        const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();
        if (focusedRowNodeL.data.state !== "insert") {
          await showDialog("alter", "신규건만 삭제할 수 있습니다.");
          return;
        }

        const focusedNodeD = getGridCurrent("grid4").focusedRowNode();

        getGridCurrent("grid3").forEachNode((node: RowNode) => {
          if (
            node.data.cd_item === focusedNodeD.data?.cd_item &&
            node.data.cd_wh === focusedNodeD.data?.cd_wh &&
            node.data.no_barcode === focusedNodeD.data?.no_barcode &&
            node.data.dt_io === focusedNodeD.data?.dt_io_origin
          ) {
            node.setDataValue(
              "qt_inv",
              node.data.qt_inv + focusedNodeD.data?.qt_good
            );

            if (node.data.fg_unit === "SN") {
              node.setDataValue("qt_isu", 1);
            }

            focusedRowNodeL.setDataValue(
              "qt_good",
              focusedRowNodeL.data?.qt_good - focusedNodeD.data?.qt_good
            );
            return;
          }
        });
        getGridCurrent("grid4").deleteSelectedRows();
        getGridCurrent("grid3").applyFilters({
          cd_item: focusedRowNodeL.data.cd_item,
          cd_wh: focusedRowNodeL.data.cd_wh,
          qt_inv: "notZero",
        });
        break;
    }
  };
  //#endregion

  //#region onBarcodeEnter
  const onBarcodeEnter = async (e: any) => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }

    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    getGridCurrent("grid3").applyFilters({
      cd_item: focusedRowNodeL.data.cd_item,
      cd_wh: focusedRowNodeL.data.cd_wh,
      qt_inv: "notZero",
    });

    const barcode = e;

    if (barcode === "" || barcode === null || barcode === undefined) {
      return;
    }

    // 스캔로직
    if (!switchRef.current?.getChecked()) {
      selectInv("S", barcode);
    } else {
      getGridCurrent("grid3").applyLikeFilters(
        {
          cd_item: focusedRowNodeL.data.cd_item,
          cd_wh: focusedRowNodeL.data.cd_wh,
          qt_inv: "notZero",
          no_barcode: barcode,
          no_ct: barcode,
          no_plt: barcode,
        },
        ["no_barcode", "no_ct", "no_plt"]
      );
    }
  };
  //#endregion

  //#region onSnButtonClick
  const onSnButtonClick = async (id: string) => {
    switch (id) {
      case "Fifo":
        selectInv("F", "");
    }
  };
  //#endregion

  //#region Method
  const selectInv = (id: string, barcode: string) => {
    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    if (focusedRowNodeL == null) {
      showDialog("alter", "선택된 행이 없습니다.");
      return false;
    }

    if (focusedRowNodeL.data.state !== "insert") {
      showDialog("alter", "신규 행에서만 수정 가능합니다.");
      return false;
    }

    let selectedMap = new Map<string, number>(); // 재고선택 키/수량 Map
    let qtAdd = 0,
      qtRemain = 0;

    switch (id) {
      case "F": // 선입선출
        if (numberBoxFifoRef.current.getValue == 0) {
          showDialog("alter", "수량이 입력되지 않았습니다.");
          return;
        }
        qtRemain = Number(numberBoxFifoRef.current.getValue);
        break;
      case "C": // 재고선택
        getGridCurrent("grid3").stopEditing();
        const selectedLotSn = getGridCurrent("grid3").getCheckedRows();
        if (selectedLotSn.length < 1) {
          showDialog("alter", "선택된 재고가 없습니다.");
          return false;
        }

        selectedLotSn.forEach((row) => {
          const key = `${row.no_barcode}_${row.no_ct}_${row.no_plt}_${row.dt_io}`;
          // 사용자가 그리드3에 입력한 '출고예정수량(qt_isu)'을 가져옴
          selectedMap.set(key, Number(row.qt_isu) || 0);
        });
        break;
      case "S": // 스캔
        // 요청량이 없는 화면
        qtRemain = -1;
        break;
    }

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();

    getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
      const row = node.data;
      let qtIsu = 0;

      switch (id) {
        case "F": // 선입선출
          if (qtRemain <= 0) return;

          qtIsu = Math.min(row.qt_inv, qtRemain); // 재고량이 적으면 재고량 아니면 출고잔량
          break;
        case "C": // 선택
          const key = `${row.no_barcode}_${row.no_ct}_${row.no_plt}_${row.dt_io}`;
          if (!selectedMap.has(key)) return;

          qtIsu = selectedMap.get(key) || 0; // 선택량 가져옴
          break;
        case "S": // 스캔
          // qtRemain === -1은 요청량 없는 건임 바코드찍은만큼 전체 출고
          if (
            String(row.no_barcode ?? "").toLowerCase() ===
              barcode.toLowerCase() ||
            String(row.no_ct ?? "").toLowerCase() === barcode.toLowerCase() ||
            String(row.no_plt ?? "").toLowerCase() === barcode.toLowerCase()
          ) {
            if (qtRemain !== -1) {
              if (qtRemain <= 0) return;

              qtIsu = Math.min(row.qt_inv, qtRemain); // 재고량이 적으면 재고량 아니면 출고잔량
            } else {
              qtIsu = row.qt_inv;
            }
          }
          break;
      }

      if (qtIsu <= 0) return;

      const newRow = {
        cd_firm: focusedRowNodeL.data?.cd_firm,
        cd_biz: focusedRowNodeH.data?.cd_biz,
        no_req: focusedRowNodeL.data?.no_req,
        no_line: focusedRowNodeL.data?.no_line,
        cd_item: focusedRowNodeL.data?.cd_item,
        cd_wh: focusedRowNodeL.data?.cd_wh,
        no_barcode: node.data.no_barcode,
        fg_unit: node.data.fg_unit,
        qt_good: qtIsu,
        no_ct: node.data.no_ct,
        no_plt: node.data.no_plt,
        no_io: node.data.no_src, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
        no_line_io: node.data.no_line_src, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
        dt_io_origin: node.data.dt_io, // 원 LOT 입고일자
      };

      // qtRemain === -1은 요청량 없는 건
      if (qtRemain !== -1) {
        qtRemain -= qtIsu; // 잔량 빼주고
      }
      qtAdd += qtIsu; // 총 출고량 더하고

      node.setDataValue("qt_isu", 0);
      node.setDataValue("qt_inv", node.data.qt_inv - qtIsu);

      let isExist = false;
      // 기존항목에 있으면 수량만 업데이트
      getGridCurrent("grid4").forEachFilteredNode((node4: RowNode) => {
        if (
          node4.data.cd_item === focusedRowNodeL.data?.cd_item &&
          node4.data.cd_wh === focusedRowNodeL.data?.cd_wh &&
          node4.data.no_barcode === node.data.no_barcode &&
          node4.data.dt_io_origin === node.data.dt_io &&
          node4.data.no_ct === node.data.no_ct &&
          node4.data.no_plt === node.data.no_plt
        ) {
          node4.setDataValue("qt_good", node4.data.qt_good + qtIsu);
          isExist = true;
          return;
        }
      });
      if (!isExist) {
        getGridCurrent("grid4").addRow(newRow);
      }
    });

    focusedRowNodeL.setDataValue(
      "qt_good",
      focusedRowNodeL.data?.qt_good + qtAdd
    );
    numberBoxFifoRef.current.setValue(0);

    getGridCurrent("grid3").clearCheckBox();
    getGridCurrent("grid3").applyFilters({
      cd_item: focusedRowNodeL.data.cd_item,
      cd_wh: focusedRowNodeL.data.cd_wh,
      qt_inv: "notZero",
    });

    if (id === "S") {
      if (qtAdd === 0) {
        showDialog("alter", "바코드 정보를 찾을 수 없습니다.");
      }
    }
  };

  const resetRowData = (): any => {
    if (getGridCurrent("grid4").getDisplayedRowCount() < 1) {
      return;
    }

    getGridCurrent("grid4").forEachFilteredNode((node4: RowNode) => {
      getGridCurrent("grid3").forEachNode((node: RowNode) => {
        if (
          node4.data.cd_item === node.data?.cd_item &&
          node4.data.cd_wh === node.data?.cd_wh &&
          node4.data.no_barcode === node.data?.no_barcode
        ) {
          node.setDataValue("qt_inv", node.data.qt_inv + node4.data.qt_good);

          if (node.data?.fg_unit === "SN") {
            node.setDataValue("qt_isu", 1);
          }
          return;
        }
      });
    });
    getGridCurrent("grid4").deleteAllRows();

    getGridCurrent("grid3").applyFilters({
      cd_item: "",
      cd_wh: "",
    });
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRowH,
          onCellValueChanged: onCellValueChangedH,
          onFocusedRowChanged: onFocusedRowChangedH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowL,
          onCellValueChanged: onCellValueChangedL,
          onFocusedRowChanged: onFocusedRowChangedL,
          onBtnAddClick: onGridLBtnsClick,
          onBtnDeleteClick: onGridLBtnsClick,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: lotSnData,
        events: {
          onCellValueChanged: onCellValueChangedLotSn,
          onRowDataUpdated: onRowDataUpdatedLotSn,
        },
      },
      grid4: {
        columns: columns.grid4,
        dataSource: dataSources.grid4,
        events: {
          onBtnAddClick: onGridDBtnsClick,
          onBtnDeleteClick: onGridDBtnsClick,
        },
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="35.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{ rowSelection: "single", title: "인계정보" }}
            events={grids.grid1.events}
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["no_req"] }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="55.5%">
          <div className="flex w-full h-full min-w-0 space-x-1.5">
            <SnResizeBar direction={"width"} id="panel3" orgSize="60%">
              <SnGrid
                ref={gridRefs.current["grid2"]} // Grid Name
                columnConfig={grids.grid2.columns} // 컬럼 설정 전달
                rowData={grids.grid2.dataSource} // Datasource
                etcProps={{ rowSelection: "multiple", title: "상세정보" }}
                events={grids.grid2.events}
                filter={{
                  refs: [gridRefs.current["grid4"]],
                  columns: ["no_req", "no_line"],
                }}
              />
            </SnResizeBar>
            <div className="flex-1 min-w-0 flex flex-col h-full space-y-1.5">
              <SnResizeBar direction={"height"} id="panel4" orgSize="50%">
                <SnGrid
                  ref={gridRefs.current["grid3"]} // Grid Name
                  columnConfig={grids.grid3.columns} // 컬럼 설정 전달
                  rowData={lotSnData} // Datasource
                  etcProps={{
                    rowSelection: "single",
                    title: "재고리스트",
                    ischeckboxSelection: true,
                  }}
                  events={grids.grid3.events}
                  header={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <SnSwitch
                        ref={switchRef}
                        labelCheck="검색"
                        labelUncheck="스캔"
                        defaultChecked={false}
                      />
                      <div>
                        <SnBarcodeBox
                          onEnter={onBarcodeEnter}
                          inputRef={txtBarcodeRef}
                        />
                      </div>
                      <div className="w-[80px]">
                        <SnNumberBox
                          inputRef={numberBoxFifoRef}
                          customFormat="QT"
                        />
                      </div>
                      <div>
                        <SnButton
                          text="선입선출"
                          onClick={() => {
                            onSnButtonClick("Fifo");
                          }}
                        />
                      </div>
                    </div>
                  }
                />
              </SnResizeBar>
              <div className="flex-1 min-h-0">
                <SnGrid
                  ref={gridRefs.current["grid4"]} // Grid Name
                  columnConfig={grids.grid4.columns} // 컬럼 설정 전달
                  rowData={grids.grid4.dataSource} // Datasource
                  etcProps={{ rowSelection: "single", title: "자재정보" }}
                  events={grids.grid4.events}
                />
              </div>
            </div>
          </div>
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
  //#endregion
});

export default SN_MTL_MOW_REG_007;
